import { useRef } from 'react';
import { setIn, useField, FormikValues, useFormikContext } from 'formik';
import { ReactGoogleAutocompleteProps } from 'react-google-autocomplete';

import { api } from 'api';
import { useAlert } from 'hooks';
import { CommonFieldProps } from 'types';
import { controlError } from 'utils/error';
import {
  AddressAutoCompleteInput,
  AddressAutocompleteInputProps,
} from 'components/address-autocomplete-input';

export interface AddressAutoCompleteInputFieldProps
  extends AddressAutocompleteInputProps, Partial<CommonFieldProps> {
  zipFieldName?: string;
  cityFieldName?: string;
  stateFieldName?: string;
  isAddressAutoFill?: boolean;
  countryCodeFieldName?: string;
  /**
   * True once a suggestion filled the address, false while it is typed by
   * hand (the form can then let City, State and ZIP be typed too).
   */
  onPickedChange?: (picked: boolean) => void;
  onTaxSelected?: (zip: string, tax: string) => void;
}

export const AddressAutoCompleteInputField = ({
  onTaxSelected,
  onPickedChange,
  name = 'address',
  label = 'Address',
  zipFieldName = 'zip',
  countryCodeFieldName,
  cityFieldName = 'city',
  stateFieldName = 'state',
  isAddressAutoFill = true,
  controlErrorLabel = true,
  placeholder = 'Add address',
  ...props
}: AddressAutoCompleteInputFieldProps) => {
  const [field, meta] = useField(name);
  const { setValues } = useFormikContext<FormikValues>();
  const { showErrorAlert } = useAlert();

  const selection = useRef(0);

  const error = controlError(meta, name, label, controlErrorLabel);

  const onPlaceSelected: ReactGoogleAutocompleteProps['onPlaceSelected'] =
    async (place) => {
      const currentSelection = ++selection.current;
      if (!place.formatted_address || !place.address_components?.length) {
        showErrorAlert(
          'Please select a complete address or enter the address details manually.'
        );
        onPickedChange?.(false);
        return;
      }
      const component = (type: string) =>
        place.address_components?.find(
          (item: { types: string[]; long_name: string; short_name: string }) =>
            item.types.includes(type)
        );
      const postal = component('postal_code');
      const candidates = [postal?.long_name, postal?.short_name];
      const baseZip =
        candidates.find(
          (value) => value && /^\d{5}(?:-?\d{4})?$/.test(value)
        ) ??
        postal?.long_name ??
        postal?.short_name ??
        '';
      const suffix = component('postal_code_suffix')?.long_name;
      const zip =
        /^\d{5}$/.test(baseZip) && suffix && /^\d{4}$/.test(suffix)
          ? `${baseZip}-${suffix}`
          : baseZip;
      const cityTypes = [
        'sublocality_level_1',
        'locality',
        'sublocality',
        'neighborhood',
        'administrative_area_level_2',
      ];
      const city =
        cityTypes.map((type) => component(type)?.long_name).find(Boolean) ?? '';
      await setValues((previous) => {
        let next = setIn(previous, name, place.formatted_address);
        if (isAddressAutoFill) {
          next = setIn(next, cityFieldName, city);
          next = setIn(
            next,
            stateFieldName,
            component('administrative_area_level_1')?.short_name ?? ''
          );
          next = setIn(next, zipFieldName, zip);
          if (countryCodeFieldName) {
            next = setIn(
              next,
              countryCodeFieldName,
              component('country')?.short_name ?? 'US'
            );
          }
        }
        return next;
      });
      // A suggestion without a valid ZIP still needs the ZIP typed.
      onPickedChange?.(!isAddressAutoFill || /^\d{5}(?:-?\d{4})?$/.test(zip));
      if (isAddressAutoFill && !/^\d{5}(?:-?\d{4})?$/.test(zip)) {
        showErrorAlert(
          'The address is missing a valid ZIP. Please check the ZIP before continuing.'
        );
      }
      if (!onTaxSelected || !/^\d{5}(?:-?\d{4})?$/.test(zip)) {
        return;
      }
      try {
        const { data: taxRate } = await api.tax.getTaxByZipcode({
          zip: zip.slice(0, 5),
        });
        if (currentSelection !== selection.current) {
          return;
        }
        const rate = taxRate?.data?.rate;
        const numericRate = Number(rate);
        if (
          rate != null &&
          String(rate).trim() &&
          Number.isFinite(numericRate) &&
          numericRate >= 0
        ) {
          onTaxSelected(zip, (numericRate * 100).toFixed(2));
        } else {
          showErrorAlert(
            'Could not auto-fill tax rate. Please enter it manually.'
          );
        }
      } catch {
        if (currentSelection === selection.current) {
          showErrorAlert(
            'Could not auto-fill tax rate. Please enter it manually.'
          );
        }
      }
    };

  return (
    <AddressAutoCompleteInput
      error={error}
      label={label}
      placeholder={placeholder}
      onPlaceSelected={onPlaceSelected}
      {...field}
      onChange={(event) => {
        // Typing drops a pick's tax lookup still on its way.
        selection.current += 1;
        field.onChange(event);
        onPickedChange?.(false);
      }}
      {...props}
    />
  );
};
